import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import SearchAlertsTable from './table/SearchAlertsTable';
import { setQueryString, useQueryString } from '../../common/helpers/queryString';
import CommonButton from '../../common/components/CommonButton';
import RouterPaths from '../../constants/RouterPaths';
import {
  clearCopiedSearchAlertState,
  clearEditSearchAlertState,
  copyCopiedSearchAlertState,
  removeSearchAlert,
  searchSearchAlerts,
} from './SearchAlertActions';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import redPlusIcon from '../../assets/img/icons/red-plus.svg';
import AddSearchAlertModal from './modals/AddSearchAlertModal';

const INITIAL_OFFSET = 0;
const MODAL_MODES = {
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
};

function SearchAlertsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQueryString();
  const dispatch = useDispatch();

  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null, // 'add' | 'edit' | 'delete'
    selectedAlert: null,
  });

  const items = useSelector((state) => state.searchAlerts.get('searchAlerts'));
  const totalItems = useSelector((state) => state.searchAlerts.get('searchAlertsTotalItems'));

  const [searchParams, setSearchParams] = useState({
    offset: query.get('page') ? +query.get('page') - 1 : INITIAL_OFFSET,
  });

  const handleRefresh = useCallback(() => {
    dispatch(searchSearchAlerts(searchParams));
  }, [dispatch, searchParams]);

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      mode: null,
      selectedAlert: null,
    });
    dispatch(clearCopiedSearchAlertState());
    dispatch(clearEditSearchAlertState());
  }, [dispatch]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  const changeOffset = useCallback(
    (value) => {
      setSearchParams((prevState) => ({ ...prevState, offset: value }));
      navigate(setQueryString(location, 'page', value + 1));
    },
    [navigate, location],
  );

  const removeItemHandler = useCallback(
    (id) => {
      const alert = items?.find((item) => item.id === id);
      if (alert) {
        setModalState({
          isOpen: true,
          mode: MODAL_MODES.DELETE,
          selectedAlert: alert,
        });
      }
    },
    [items],
  );

  const copyItemHandler = useCallback(
    (values) => {
      if (values) {
        dispatch(clearCopiedSearchAlertState());
        dispatch(copyCopiedSearchAlertState(values));
        navigate(RouterPaths.CREATE_SEARCH_ALERT);
      }
    },
    [dispatch, navigate],
  );

  const editItemHandler = useCallback(
    (id) => {
      const alert = items?.find((item) => item.id === id);
      if (alert) {
        setModalState({
          isOpen: true,
          mode: MODAL_MODES.EDIT,
          selectedAlert: alert,
        });
      }
    },
    [items],
  );

  const handleAddButtonClick = useCallback(() => {
    dispatch(clearCopiedSearchAlertState());
    dispatch(clearEditSearchAlertState());
    setModalState({
      isOpen: true,
      mode: MODAL_MODES.ADD,
      selectedAlert: null,
    });
  }, [dispatch]);

  const handleModalSave = useCallback(async () => {
    if (modalState.mode === MODAL_MODES.DELETE && modalState.selectedAlert?.id) {
      try {
        await dispatch(removeSearchAlert(modalState.selectedAlert.id));
        handleRefresh();
      } catch (error) {
        console.error('Failed to delete search alert:', error);
      }
    } else {
      handleRefresh();
    }
    closeModal();
  }, [modalState.mode, modalState.selectedAlert, dispatch, handleRefresh, closeModal]);

  const handleModalEdit = useCallback(() => {
    handleRefresh();
    closeModal();
  }, [handleRefresh, closeModal]);

  const tableData = useMemo(
    () => ({
      items,
      total: totalItems,
    }),
    [items, totalItems],
  );

  const modalProps = useMemo(() => {
    const { mode, selectedAlert } = modalState;
    return {
      isOpen: modalState.isOpen,
      toggle: closeModal,
      onSave: handleModalSave,
      onEdit: handleModalEdit,
      initialValues: selectedAlert,
      isEdit: mode === MODAL_MODES.EDIT,
      isDelete: mode === MODAL_MODES.DELETE,
    };
  }, [modalState, closeModal, handleModalSave, handleModalEdit]);

  return (
    <>
      <AddSearchAlertModal {...modalProps} />
      <div className="settings-page-content--search-alerts">
        <SearchAlertsTable
          data={tableData}
          offset={searchParams.offset}
          changeOffset={changeOffset}
          copyItemHandler={copyItemHandler}
          editItemHandler={editItemHandler}
          setSearchAlertToDelete={removeItemHandler}
        />
      </div>
      <Row>
        <Col className="d-flex justify-content-start align-items-center mt-3">
          <CommonButton
            className="text-sm"
            label="ADD NEW SEARCH ALERT"
            variant={CommonButtonVariants.OUTLINED_RED}
            icon={redPlusIcon}
            iconAlt="Add New Alert"
            iconClassName="common-button__icon-red"
            iconLeft={true}
            handleClick={handleAddButtonClick}
          />
        </Col>
      </Row>
    </>
  );
}
export default SearchAlertsPage;
